import { useState } from "react";
import { Button } from "../ui/button";
import { StatusBadge } from "./StatusBadge";
import { AudioPlayer } from "./AudioPlayer";
import { Volume2, Loader2, RefreshCw, AlignLeft, CheckCircle2, AlertCircle, Clock } from "lucide-react";
import api from "../../lib/api";
import { toast } from "sonner";

export function WalkthroughAudioSection({ session, storyboard, walkthroughAudio, alignment, generating, onGenerate, onRefresh }) {
  const [alignRunning, setAlignRunning] = useState(false);
  const walkthroughAudioReady = walkthroughAudio.status === 'ready';
  const alignStatus = alignment?.status;

  const handleRunAlignment = async () => {
    setAlignRunning(true);
    try {
      await api.post(`/sessions/${session.id}/production/run-alignment`);
      toast.success("Transcript alignment started...");
      const poll = setInterval(async () => {
        try {
          const res = await api.get(`/sessions/${session.id}/production/alignment-status`);
          if (res.data.status === 'ready' || res.data.status === 'failed') {
            clearInterval(poll);
            setAlignRunning(false);
            onRefresh();
            if (res.data.status === 'ready') {
              toast.success(`Alignment complete: ${res.data.meta?.matched_count || 0} segments matched`);
            } else {
              toast.error(res.data.error || "Alignment failed");
            }
          }
        } catch {}
      }, 3000);
      setTimeout(() => { clearInterval(poll); setAlignRunning(false); }, 120000);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to start alignment");
      setAlignRunning(false);
    }
  };

  const alignStatusLabel = {
    processing: 'Processing...',
    transcribing: 'Transcribing audio...',
    aligning: 'Aligning segments...',
    ready: 'Aligned',
    failed: 'Alignment failed',
  };

  return (
    <section className="bg-white rounded-xl border border-[#E2E4E7] overflow-hidden" data-testid="production-walkthrough-audio">
      <div className="px-5 py-4 border-b border-[#E2E4E7] flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center">
            <Volume2 size={18} className="text-blue-600" />
          </div>
          <div>
            <h3 className="font-semibold text-[#020B23] text-sm" style={{ fontFamily: 'Sora, sans-serif' }}>Walkthrough Voiceover</h3>
            <p className="text-xs text-[#94969C]">Full narration for all walkthrough shots</p>
          </div>
        </div>
        <StatusBadge status={walkthroughAudio.status} />
      </div>
      <div className="p-5 space-y-3">
        <div className="bg-[#F3F3F9] rounded-lg p-3">
          <p className="text-xs text-[#94969C] uppercase tracking-wide mb-1 font-medium">Full Script</p>
          <p className="text-sm text-[#1A2238] leading-relaxed line-clamp-3">{storyboard.voiceover_script?.full_body_text || 'Generate storyboard first'}</p>
        </div>
        <div className="flex items-center gap-3">
          <Button size="sm" onClick={onGenerate}
            disabled={generating}
            data-testid="gen-walkthrough-audio-btn"
            className="text-xs bg-[#4D11F6] hover:bg-[#3A0BC0] text-white h-7">
            {generating ? <><Loader2 size={11} className="mr-1 animate-spin" /> Generating...</> :
             walkthroughAudioReady ? <><RefreshCw size={11} className="mr-1" /> Regenerate</> :
             walkthroughAudio.status === 'failed' ? <><RefreshCw size={11} className="mr-1" /> Retry</> :
             <><Volume2 size={11} className="mr-1" /> Generate</>}
          </Button>
          {walkthroughAudioReady && <AudioPlayer url={walkthroughAudio.url} />}
        </div>

        {/* Transcript Alignment Section */}
        {walkthroughAudioReady && (
          <div className="border-t border-[#E2E4E7] pt-3">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <AlignLeft size={14} className="text-[#676970]" />
                <span className="text-xs font-semibold text-[#020B23]" style={{ fontFamily: 'Sora, sans-serif' }}>
                  Voiceover-to-Clip Alignment
                </span>
              </div>
              {alignStatus && (
                <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${
                  alignStatus === 'ready' ? 'bg-emerald-50 text-emerald-600' :
                  alignStatus === 'failed' ? 'bg-red-50 text-red-600' :
                  'bg-amber-50 text-amber-600'
                }`} data-testid="alignment-status-badge">
                  {alignStatus === 'ready' && <CheckCircle2 size={10} className="inline mr-1" />}
                  {alignStatus === 'failed' && <AlertCircle size={10} className="inline mr-1" />}
                  {['processing', 'transcribing', 'aligning'].includes(alignStatus) && <Clock size={10} className="inline mr-1" />}
                  {alignStatusLabel[alignStatus] || alignStatus}
                </span>
              )}
            </div>

            {alignStatus === 'ready' && alignment?.meta && (
              <div className="flex gap-3 mb-2">
                <span className="text-[10px] text-[#94969C]">{alignment.meta.matched_count || 0} matched</span>
                {alignment.meta.fallback_count > 0 && (
                  <span className="text-[10px] text-amber-500">{alignment.meta.fallback_count} fallback</span>
                )}
                {alignment.audio_duration_seconds && (
                  <span className="text-[10px] text-[#94969C]">{alignment.audio_duration_seconds}s audio</span>
                )}
              </div>
            )}

            {alignStatus === 'failed' && alignment?.error && (
              <p className="text-[10px] text-red-500 mb-2 break-all">{alignment.error}</p>
            )}

            <p className="text-[10px] text-[#94969C] mb-2">
              Matches each clip to its voiceover timing so video speed is adjusted to sync with narration.
            </p>

            <Button size="sm" variant="outline" onClick={handleRunAlignment}
              disabled={alignRunning || ['processing', 'transcribing', 'aligning'].includes(alignStatus)}
              data-testid="run-alignment-btn"
              className="text-xs h-7 border-[#E2E4E7]">
              {alignRunning || ['processing', 'transcribing', 'aligning'].includes(alignStatus) ? (
                <><Loader2 size={11} className="mr-1 animate-spin" /> {alignStatusLabel[alignStatus] || 'Processing...'}</>
              ) : alignStatus === 'ready' ? (
                <><RefreshCw size={11} className="mr-1" /> Re-align</>
              ) : (
                <><AlignLeft size={11} className="mr-1" /> Run Alignment</>
              )}
            </Button>
          </div>
        )}
      </div>
    </section>
  );
}
